import EventEmitter from 'events';
import FontFaceObserver from 'fontfaceobserver';

import { TextureLoader } from 'three';
import gsap from 'gsap';

import DetectController from '../../controllers/detect';

export default class Preloader extends EventEmitter {

	el = {};

	counter = 0;

	constructor({ slug, container }) {
		super();

		this.el = {
			container,
			text: container.querySelector('.preloader__text')
		};

		this.isDesktop = DetectController.isDesktop;

		this.slug = slug;

		this.data = APPDATA.webgl;

		this.loader = new TextureLoader();

		this.total = 0;

		this.currentColor = slug.includes('news/') || slug.includes('case-studies/') ? 'tan' : 'green';

		this.el.container.style = `--this-bg-color: var(--brand-${this.currentColor}); --this-text-color: var(--brand-${this.currentColor === 'tan' ? 'green' : 'tan'})`;
	}

	load() {
		const cinderblock = new FontFaceObserver('Cinderblock', { weight: 100 });
		const roobert = new FontFaceObserver('Roobert', { weight: 400 });
		const roobertSemiBold = new FontFaceObserver('Roobert Semi Bold', { weight: 600 });

		Promise.all([
			cinderblock.load(null, 1000),
			roobert.load(null, 1000),
			roobertSemiBold.load(null, 1000)
		]).then(() => {
			if (!this.isDesktop) {
				this.onComplete();
			}
		});

		if (this.isDesktop) {
			this.data.forEach((data, index) => {
				if (data.heroes && data.heroes.length) {
					this.parse(data.heroes, index);
				}

				if (data.splitsNext && data.splitsNext.length) {
					this.parse(data.splitsNext, index);
				}

				if (data.splitsFeature && data.splitsFeature.length) {
					this.parse(data.splitsFeature, index);
				}

				if (data.galleries && data.galleries.length) {
					this.parse(data.galleries, index);
				}

				if (data.fullWidths && data.fullWidths.length) {
					this.parse(data.fullWidths, index);
				}
			});
		}
	}

	parse(modules, index) {
		for (const module of modules) {
			const loads = [];
			const container = {
				uid: module.uid,
				slug: module.slug,
				type: module.type,
				pageIndex: index,
				textures: []
			};

			this.total += module.assets.length;

			for (const asset of module.assets) {
				asset.parentUid = module.uid;
				loads.push(new Promise((resolve) => {
					this.loadTexture(asset, (texture) => {
						texture.slug = module.slug;
						texture.parentIndex = index;
						texture.parentUid = module.uid;
						texture.parentType = module.type;
						container.textures.push(texture);
						this.onProgress();
						resolve();
					});
				}));
			}

			Promise.all(loads).then(() => { // eslint-disable-line
				this.emit('loadtexture', container);
			});
		}
	}

	loadTexture(asset, onComplete) {
		this.loader.load(asset.url, texture => {
			texture.uid = asset.uid;
			texture.imageWidth = asset.width;
			texture.imageHeight = asset.height;
			onComplete(texture);
		});
	}

	onProgress() {
		this.counter += 1;
		this.el.text.textContent = `${(Math.round(this.counter / this.total * 100))}`;

		if (this.counter === this.total) {
			this.onComplete();
		}
	}

	onComplete() {
		this.hide();
	}

	show() {
		this.timelineIn = gsap.timeline();

		if (this.isDesktop) {
			this.el.text.innerHTML = '0';

			gsap.set(this.el.text, {
				autoAlpha: 1
			});

			this.timelineIn.fromTo(
				this.el.text, 1.5,
				{
					y: '100%'
				},
				{
					ease: 'expo.out',
					y: '0%'
				}
			);
		}

		this.timelineIn.call(() => this.load());
	}

	hide() {
		if (this.timelineIn) {
			this.timelineIn.kill();
			this.timelineIn = null;
		}

		this.timelineOut = gsap.timeline({
			delay: 0
		});

		if (this.isDesktop) {
			this.timelineOut
				.to(this.el.text, 1.5, {
					ease: 'expo.inOut',
					y: '-100%'
				});
		}

		this.timelineOut
				.call(() => {
					this.emit('complete');
				})
				.to(this.el.container, 2, {
					ease: 'expo.inOut',
					y: '-100%'
				});

		this.timelineOut.call(() => {
			document.body.removeChild(this.el.container);
		});
	}

}
